<template>
	<div class="pg-btn-holder" :class="`pg-btn-holder--${pgDetails.methodName}`">
		<button
			:id="pgDetails.methodName"
			class="btn btn-block btn-default pg-button"
			tabindex="1"
			:disabled="btnDisabled"
			:class="{
				disabled: btnDisabled,
				maintenance: maintenance,
				'has-venmo': isVenmo,
			}"
			@click="handleClicked"
		>
			<div v-if="isPgLoading" class="spinner-circle-container">
				<span class="spinner-circle"></span>
			</div>

			<template v-else>
				<div class="pg-icon"></div>

				<div v-if="maintenance" class="maintenance-message">
					<p>
						{{ $t('checkout.under-maintenance') }}
						<span>{{ $t('checkout.try-again-later') }}</span>
					</p>
				</div>
				<span class="pg-label">{{
					$te(`checkout.${pgDetails.methodName}`)
						? $t(`checkout.${pgDetails.methodName}`)
						: pgDetails.displayName
				}}</span>
			</template>
		</button>
	</div>
</template>

<script>
import { mapState } from 'vuex';

export default {
	name: 'PgButton',
	props: {
		loading: {
			type: Boolean,
			default: false,
		},
		pgDetails: {
			type: Object,
			default: () => ({}),
			required: true,
		},
	},
	computed: {
		...mapState(['isAndroid', 'isIOS', 'isWebkit', 'isChromeIOS', 'isFirefoxIOS', 'isOperaIOS', 'isMercury']),
		isVenmo() {
			try {
				return (
					this.pgDetails.displayName.indexOf('Venmo') > -1 &&
					(this.isAndroid ||
						(this.isIOS &&
							this.isWebkit &&
							!this.isChromeIOS &&
							!this.isFirefoxIOS &&
							!this.isOperaIOS &&
							!this.isMercury))
				);
			} catch {
				return false;
			}
		},
		isPgLoading() {
			return (
				this.loading &&
				this.$store.state.selectedPg &&
				this.$store.state.selectedPg.methodName === this.pgDetails.methodName
			);
		},
		maintenance() {
			return this.pgDetails && this.pgDetails.status !== 'active';
		},
		btnDisabled() {
			return this.maintenance;
		},
	},
	methods: {
		handleClicked() {
			this.$emit('click-pg', this.pgDetails);
		},
	},
};
</script>

<style lang="less" scoped>
.pg-btn-holder {
	padding: 5px;
	position: relative;
	&--xsolla {
		order: 10;
	}
	.spinner-circle-container {
		top: 0;
		left: 0;
		position: absolute;
		width: 100%;
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		opacity: 1 !important;
		.spinner-circle {
			width: 40px;
			height: 40px;
			border-width: 6px;
		}
	}
	.pg-button {
		position: relative;
		height: 80px;
		border: 1px solid rgba(#fff, 20%);
		background-color: rgba(#fff, 5%);
		border-radius: 2px;
		padding: 0;
		margin: 0;
		transition: all 0.3s;

		&:hover {
			background-color: fade(#e6e6e6, 15%);
		}

		&:active {
			border: 1px solid fade(#fff, 20%);
			background-color: fade(#e6e6e6, 18%);
		}
		&.disabled {
			opacity: 0.3;
			&:hover,
			&:focus {
				border: 1px solid #474747;
				background-color: fade(#e6e6e6, 5%);
			}

			.pg-label {
				opacity: 0.6;
			}
			&.maintenance {
				opacity: 1;
				.pg-icon {
					opacity: 0.8;
				}
				.pg-label {
					opacity: 0.2;
				}
			}
		}

		.maintenance-message {
			position: absolute;
			top: 0;
			left: 0;
			background-color: fade(#000, 80%);
			width: 100%;
			height: 100%;
			p {
				color: #e5e5e5;
				text-transform: uppercase;
				position: absolute;
				top: 50%;
				left: 0;
				transform: translateY(-50%);
				width: 100%;
				white-space: normal;
				span {
					display: block;
					position: relative;
					top: 0 !important;
					text-transform: none;
					font-size: 12px;
					color: #b2b2b2;
				}
				&:before {
					opacity: 0.2;
				}
			}
		}

		.pg-label {
			display: none;
			font-size: 14px;
			color: #fff;
			font-weight: normal;
			line-height: 1.2;
		}
		.pg-icon {
			width: 100%;
			height: 100%;
			background-image: url('~@/images/pg-list/sandbox.png');
			background-size: auto auto;
			background-position: center;
			background-repeat: no-repeat;
		}

		&#molglobal,
		&#west_Razer {
			.pg-icon {
				background-image: url('~@/images/pg-list/razergold.png');
			}
		}
		&#paysafecard {
			.pg-icon {
				background-image: url('~@/images/pg-list/paysafecard.png');
			}
		}
		&#skrill {
			.pg-icon {
				background-image: url('~@/images/pg-list/skrill.png');
			}
		}
		&#boacompra {
			.pg-icon {
				background-image: url('~@/images/pg-list/boacompra.png');
			}
		}
		&#mycard {
			.pg-icon {
				background-image: url('~@/images/pg-list/mycard.png');
				background-position: 50% 62%;
			}
		}
		&#bank_of_ayudhya {
			padding: 10px 0;
			.pg-icon {
				background-image: url('~@/images/pg-list/bank_of_ayudhya.png');
				background-size: contain;
			}
		}
		&#bangkok_bank {
			padding: 10px 0;
			.pg-icon {
				background-image: url('~@/images/pg-list/bangkok_bank.png');
				background-size: contain;
			}
		}
		&#krung_thai_bank {
			padding: 10px 0;
			.pg-icon {
				background-image: url('~@/images/pg-list/krung_thai_bank.png');
				background-size: contain;
			}
		}
		&#the_siam_commercial_bank {
			padding: 10px 0;
			.pg-icon {
				background-image: url('~@/images/pg-list/the_siam_commercial_bank.png');
				background-size: contain;
			}
		}
		&#kasikornbank {
			padding: 10px 0;
			.pg-icon {
				background-image: url('~@/images/pg-list/kasikornbank.png');
				background-size: contain;
			}
		}
		&#true_wallet {
			padding: 10px 0;
			.pg-icon {
				background-image: url('~@/images/pg-list/true_wallet.png');
				background-size: contain;
			}
		}
		&#th_razer_gold {
			.pg-icon {
				background-image: url('~@/images/pg-list/th_razer_gold.png');
			}
		}
		&#mol_12call {
			padding: 10px 0;
			.pg-icon {
				background-image: url('~@/images/pg-list/mol_12call.png');
				background-size: contain;
			}
		}
		&#mol_truemoney {
			padding: 10px 0;
			.pg-icon {
				background-image: url('~@/images/pg-list/mol_truemoney.png');
				background-size: contain;
			}
		}
		&#truemoney {
			.pg-icon {
				background-image: url('~@/images/pg-list/truemoney.png');
			}
		}
		&#mycard_extend {
			.pg-icon {
				background-image: url('~@/images/pg-list/mycard.png');
				background-position: 50% 62%;
			}
		}
		&#ingenico,
		&#braintree,
		&#xsolla_credit,
		&#th_xsolla,
		&#sea_xsolla {
			display: flex;
			justify-content: center;
			align-items: center;
			flex-wrap: nowrap;
			padding-left: 10px;
			padding-right: 10px;
			.pg-icon {
				background-image: url('~@/images/pg-list/card.png');
				max-width: 98px;
				height: 54px;
				background-size: contain;
				flex-basis: auto;
				flex-shrink: 1;
			}
			.pg-label {
				display: inline-block;
				position: relative;
				width: auto;
				padding-left: 10px;
			}
			&.has-venmo {
				.pg-icon {
					background-image: url('~@/images/pg-list/creditvenmo.png');
					width: 198px;
					max-width: 198px;
					height: 100%;
					background-size: auto auto;
				}
				.pg-label {
					display: none;
				}
			}
		}
		&#nexon_game_card {
			.pg-icon {
				background-image: url('~@/images/pg-list/karmakoin-nexon-gc.png');
			}
		}
		&#paypal {
			.pg-icon {
				background-image: url('~@/images/pg-list/paypal.png');
			}
		}
		&#xsolla {
			padding: 8px 10px 10px 10px;
			.pg-icon {
				display: none;
				background-image: url('~@/images/pg-list/xsolla.png');
			}
			.pg-label {
				display: inline-block;
				white-space: normal;
			}
		}
		&#xsolla_bitpay {
			.pg-icon {
				background-image: url('~@/images/pg-list/bitpay.png');
			}
		}
		&#nexoncards {
			.pg-icon {
				background-image: url('~@/images/pg-list/nexoncards.png');
			}
		}
		&#epin {
			.pg-icon {
				background-image: url('~@/images/pg-list/epin.png');
			}
		}
		&#gocash {
			.pg-icon {
				background-image: url('~@/images/pg-list/gocash.png');
			}
		}
		&#xsolla-gocash {
			.pg-icon {
				background-image: url('~@/images/pg-list/xsolla-gocash.png');
			}
		}
		&#xsolla-royalbank {
			.pg-icon {
				background-image: url('~@/images/pg-list/royalbank.png');
			}
		}
		&#xsolla-aunz {
			.pg-icon {
				background-image: url('~@/images/pg-list/aunz.png');
			}
		}
		&#xsolla-commonwealth {
			.pg-icon {
				background-image: url('~@/images/pg-list/commonwealth.png');
			}
		}
		&#xsolla-ing {
			.pg-icon {
				background-image: url('~@/images/pg-list/ing.png');
			}
		}
		&#xsolla-rabobank {
			.pg-icon {
				background-image: url('~@/images/pg-list/rabobank.png');
			}
		}
		&#xsolla-sofort {
			.pg-icon {
				background-image: url('~@/images/pg-list/sofort.png');
			}
		}
		&#xsolla-giropay {
			.pg-icon {
				background-image: url('~@/images/pg-list/giropay.png');
			}
		}
		&#xsolla-swedbank {
			.pg-icon {
				background-image: url('~@/images/pg-list/swedbank.png');
			}
		}
		&#xsolla-mobile {
			.pg-icon {
				background-image: url('~@/images/pg-list/paybymobile.png');
			}
		}
		&#xsolla-nordae {
			.pg-icon {
				background-image: url('~@/images/pg-list/nordea.png');
			}
		}
		&#xsolla-nordea {
			.pg-icon {
				background-image: url('~@/images/pg-list/nordea.png');
			}
		}
		&#xsolla-bbva {
			.pg-icon {
				background-image: url('~@/images/pg-list/bbva.png');
			}
		}
		&#xsolla-pay {
			.pg-icon {
				background-image: url('~@/images/pg-list/paybybank.png');
			}
		}
		&#xsolla-trustly {
			.pg-icon {
				background-image: url('~@/images/pg-list/trustly.png');
			}
		}
		&#xsolla-boku {
			.pg-icon {
				background-image: url('~@/images/pg-list/boku.png');
			}
		}
		&#xsolla-bancontact {
			.pg-icon {
				background-image: url('~@/images/pg-list/bancontact.png');
			}
		}
		&#xsolla-blik {
			.pg-icon {
				background-image: url('~@/images/pg-list/blik.png');
			}
		}
		&#xsolla-place {
			.pg-icon {
				background-image: url('~@/images/pg-list/place.png');
			}
		}
		&#xsolla-apple {
			.pg-icon {
				background-image: url('~@/images/pg-list/apple_pay.png');
			}
		}
		&#xsolla-google {
			.pg-icon {
				background-image: url('~@/images/pg-list/google_pay.png');
			}
		}
		&#xsolla-alipay {
			.pg-icon {
				background-image: url('~@/images/pg-list/ali_pay.png');
			}
		}
		&#xsolla-ideal {
			.pg-icon {
				background-image: url('~@/images/pg-list/ideal.png');
			}
		}
		&#xsolla-klarna {
			.pg-icon {
				background-image: url('~@/images/pg-list/klarna.png');
			}
		}
	}
}
</style>
